import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Modal, ScrollView, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { ThemeColors, useTheme } from '../../context/ThemeContext';
import { canteenAPI, serverMessage } from '../../services/walletApi';
import { LoadingSpinner } from '../common/LoadingSpinner';
import { Logger } from '../../utils/logger';
import { dayLabel, isVoidOrder, isoDate, monthChoices, orderHistoryView, orderItemsText } from '../../utils/canteenPlanner';
import { stamp } from '../../utils/walletFormat';
import type { CanteenOrder } from '../../types/wallet';
import { Notice } from '../wallet/WalletBits';
import { Chips, StatusPill, money } from '@shared/commerce';

/**
 * Canteen → Order history (27 Oct 2026): one month of a child's canteen orders, on the Canteen
 * screen itself (no menu of its own). A month at a time from GET canteen/my/{id}/orders?month=,
 * upcoming orders first, then day by day; tap one for every item and its status. The parent app
 * and the parent web portal alike.
 */
export function CanteenHistory({ studentId, childName, currency, refreshKey }: {
  studentId: string;
  childName: string;
  currency: string;
  /** Changes when the screen is pulled to refresh. */
  refreshKey: number;
}) {
  const { colors } = useTheme();
  const styles = useMemo(() => createStyles(colors), [colors]);
  const months = useMemo(() => monthChoices(new Date()), []);
  const [month, setMonth] = useState(months[0].value);
  const [orders, setOrders] = useState<CanteenOrder[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<CanteenOrder | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      setOrders(await canteenAPI.getOrdersForMonth(studentId, month));
    } catch (err) {
      Logger.error('Canteen', 'Failed to load the order history', err);
      setOrders([]);
      setError(serverMessage(err, 'The orders could not be loaded. Please try again.'));
    }
  }, [studentId, month]);

  useEffect(() => { setOrders(null); void load(); }, [load, refreshKey]);

  const label = months.find(m => m.value === month)?.label ?? month;
  const view = useMemo(() => orderHistoryView(orders ?? [], isoDate(new Date())), [orders]);

  const card = (o: CanteenOrder) => {
    const off = isVoidOrder(o);
    return (
      <TouchableOpacity key={o.canteenOrderId} style={[styles.ordered, off && styles.dim]} onPress={() => setOpen(o)}
        accessibilityRole="button" accessibilityHint="Opens every item in this order" testID={`canteen-history-${o.orderNumber}`}>
        <View style={styles.rowBetween}>
          <Text style={styles.forText} numberOfLines={1}>
            <Text style={styles.orderNo}>{o.orderNumber}</Text>
            {o.serveDate ? ` · For ${dayLabel(o.serveDate)}` : ''}
          </Text>
          <StatusPill orderStatus={o.orderStatus} />
        </View>
        <Text style={styles.optionName} numberOfLines={2}>{orderItemsText(o) || 'No items'}</Text>
        <View style={styles.rowBetween}>
          <Text style={styles.small}>
            {off && o.settlementStatus === 'Refunded' ? 'Refunded to the wallet' : o.orderedBy ? `Ordered by ${o.orderedBy}` : ''}
          </Text>
          <Text style={[styles.price, { color: colors.textPrimary }, off && styles.struck]}>{money(o.totalAmount, currency)}</Text>
        </View>
      </TouchableOpacity>
    );
  };

  return (
    <View style={styles.stack} testID="canteen-history">
      <Chips testID="canteen-history-month" options={months} value={month} onChange={setMonth} />

      {error && <Notice tone="error">{error}</Notice>}

      {orders === null ? <LoadingSpinner /> : (
        <>
          <View style={[styles.card, styles.summary]} testID="canteen-history-summary">
            <View style={styles.summaryCell}>
              <Text style={styles.small}>Orders</Text>
              <Text style={styles.summaryValue}>{view.summary.orders}</Text>
            </View>
            <View style={styles.summaryCell}>
              <Text style={styles.small}>Spent</Text>
              <Text style={styles.summaryValue}>{money(view.summary.spent, currency)}</Text>
            </View>
            <View style={styles.summaryCell}>
              <Text style={styles.small}>Cancelled</Text>
              <Text style={styles.summaryValue}>{view.summary.cancelled}</Text>
            </View>
          </View>

          {view.summary.orders === 0 && !error && (
            <View style={styles.card}><Text style={styles.muted}>{`No canteen orders for ${childName} in ${label}.`}</Text></View>
          )}

          {view.upcoming.length > 0 && (
            <>
              <Text style={styles.groupHead}>UPCOMING</Text>
              {view.upcoming.map(card)}
            </>
          )}
          {view.days.map(d => (
            <View key={d.date} style={styles.stack}>
              <Text style={styles.groupHead}>{d.label.toUpperCase()}</Text>
              {d.orders.map(card)}
            </View>
          ))}

          {view.summary.orders > 0 && <Text style={[styles.small, styles.center]}>Tap an order for every item</Text>}
        </>
      )}

      <Modal visible={!!open} transparent animationType="fade" onRequestClose={() => setOpen(null)}>
        <View style={styles.modalOverlay}>
          {open && (
            <View style={[styles.modal, styles.detailModal]} testID="canteen-history-details">
              <View style={styles.rowBetween}>
                <Text style={styles.modalTitle}>{`Order ${open.orderNumber}`}</Text>
                <StatusPill orderStatus={open.orderStatus} />
              </View>
              <Text style={styles.small}>
                {[open.serveDate ? `For ${dayLabel(open.serveDate)}` : null, open.orderDate ? `placed ${stamp(open.orderDate)}` : null]
                  .filter(Boolean).join(' · ')}
              </Text>
              <ScrollView style={styles.detailList}>
                {(open.items ?? []).map((i, n) => (
                  <View key={`${i.itemName}-${n}`} style={styles.detailRow}>
                    <Text style={[styles.detailText, styles.fill]}>{`${i.quantity} × ${i.itemName}`}</Text>
                    <Text style={styles.detailText}>{money(i.unitPrice * i.quantity, currency)}</Text>
                  </View>
                ))}
              </ScrollView>
              <View style={styles.rowBetween}>
                <Text style={styles.detailTotal}>Total</Text>
                <Text style={styles.detailTotal}>{money(open.totalAmount, currency)}</Text>
              </View>
              {(open.walletPaid ?? 0) > 0 && (
                <View style={styles.rowBetween}>
                  <Text style={styles.muted}>{`Paid from ${childName}'s wallet`}</Text>
                  <Text style={styles.muted}>{money(open.walletPaid, currency)}</Text>
                </View>
              )}
              {isVoidOrder(open) && open.settlementStatus === 'Refunded' && (
                <Text style={styles.muted}>{`The money went back to ${childName}'s wallet.`}</Text>
              )}
              {!!open.orderedBy && (
                <View style={styles.rowBetween}>
                  <Text style={styles.muted}>Ordered by</Text>
                  <Text style={styles.muted}>{open.orderedBy}</Text>
                </View>
              )}
              <View style={styles.modalActions}>
                <TouchableOpacity style={styles.secondaryBtn} onPress={() => setOpen(null)} testID="canteen-history-close">
                  <Text style={styles.secondaryBtnText}>Close</Text>
                </TouchableOpacity>
              </View>
            </View>
          )}
        </View>
      </Modal>
    </View>
  );
}

// The same look as the planner's cards and popups (CanteenContent).
const createStyles = (colors: ThemeColors) => StyleSheet.create({
  stack: { gap: 10 },
  fill: { flex: 1 },
  dim: { opacity: 0.6 },
  center: { textAlign: 'center' },
  muted: { fontSize: 12.5, color: colors.textSecondary },
  small: { fontSize: 11.5, color: colors.textSecondary },
  card: { backgroundColor: colors.card, borderRadius: 16, borderWidth: 1, borderColor: colors.border, padding: 12 },
  rowBetween: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 6 },
  summary: { flexDirection: 'row', justifyContent: 'space-between' },
  summaryCell: { gap: 2 },
  summaryValue: { fontSize: 16, fontWeight: '800', color: colors.textPrimary },
  groupHead: { fontSize: 11, fontWeight: '800', letterSpacing: 0.4, color: colors.textSecondary, marginTop: 4 },
  ordered: { backgroundColor: colors.card, borderRadius: 14, borderWidth: 1, borderColor: colors.border, padding: 12, gap: 6 },
  forText: { fontSize: 11.5, fontWeight: '700', color: colors.textSecondary, flexShrink: 1 },
  orderNo: { color: colors.textPrimary, fontWeight: '800' },
  optionName: { fontSize: 13, fontWeight: '700', color: colors.textPrimary },
  price: { fontSize: 12.5, fontWeight: '800' },
  struck: { textDecorationLine: 'line-through' },
  modalOverlay: { flex: 1, backgroundColor: colors.overlay, alignItems: 'center', justifyContent: 'center', padding: 20 },
  modal: { width: '100%', maxWidth: 420, backgroundColor: colors.card, borderRadius: 16, padding: 18, gap: 10 },
  detailModal: { maxHeight: '85%' },
  modalTitle: { fontSize: 16, fontWeight: '800', color: colors.textPrimary },
  modalActions: { flexDirection: 'row', justifyContent: 'flex-end', gap: 8, marginTop: 4 },
  detailList: { flexGrow: 0, flexShrink: 1, borderTopWidth: 1, borderTopColor: colors.border },
  detailRow: { flexDirection: 'row', gap: 10, paddingVertical: 8, borderBottomWidth: 1, borderBottomColor: colors.border },
  detailText: { fontSize: 13, color: colors.textPrimary },
  detailTotal: { fontSize: 14, fontWeight: '800', color: colors.textPrimary },
  secondaryBtn: { borderRadius: 10, paddingHorizontal: 16, paddingVertical: 11, borderWidth: 1, borderColor: colors.border },
  secondaryBtnText: { color: colors.textPrimary, fontSize: 13.5, fontWeight: '700' },
});
